import React, { useState, useEffect } from 'react';
import { GiHamburgerMenu } from 'react-icons/gi';  // Hamburger icon
import { RxCross1 } from 'react-icons/rx';         // Cross icon
import { RiProgress8Fill, RiProgress8Line } from 'react-icons/ri';  // Progress icons for visited and current
import { GoCircle } from "react-icons/go";

import './Header.css';  // Importing the styles

const EXTRA_SCROLL_OFFSET = 16;
const NAV_ITEMS = [
  { id: 'home', label: 'Home' },
  { id: 'about', label: 'About' },
  { id: 'education', label: 'Education' },
  { id: 'experience', label: 'Experience' },
  { id: 'portfolio', label: 'Publications' },
  { id: 'skills', label: 'Skills' },
  { id: 'projects', label: 'Projects' },
];

function Header() {
  const [activeSectionId, setActiveSectionId] = useState(NAV_ITEMS[0].id);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const updateHeaderOffset = () => {
      const navContainer = document.querySelector('.nav-container');
      const navHeight = navContainer?.getBoundingClientRect().height ?? 0;
      document.documentElement.style.setProperty(
        '--header-offset',
        `${Math.ceil(navHeight + EXTRA_SCROLL_OFFSET)}px`
      );
    };

    const updateActiveSection = () => {
      const navContainer = document.querySelector('.nav-container');
      const headerHeight = navContainer?.getBoundingClientRect().height ?? 0;
      const currentScrollMarker = window.scrollY + headerHeight + EXTRA_SCROLL_OFFSET + 1;
      const sectionIds = NAV_ITEMS
        .map(({ id }) => id)
        .filter((id) => document.getElementById(id));

      if (sectionIds.length === 0) {
        return;
      }

      const isAtBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;

      const nextActiveSection = isAtBottom
        ? sectionIds[sectionIds.length - 1]
        : sectionIds.reduce((currentId, sectionId) => {
            const sectionTop = document.getElementById(sectionId)?.offsetTop ?? 0;
            return sectionTop <= currentScrollMarker ? sectionId : currentId;
          }, sectionIds[0]);

      setActiveSectionId((currentId) =>
        currentId === nextActiveSection ? currentId : nextActiveSection
      );
    };

    updateHeaderOffset();
    updateActiveSection();

    window.addEventListener('resize', updateHeaderOffset);
    window.addEventListener('resize', updateActiveSection);
    window.addEventListener('load', updateActiveSection);
    window.addEventListener('scroll', updateActiveSection, { passive: true });

    return () => {
      window.removeEventListener('resize', updateHeaderOffset);
      window.removeEventListener('resize', updateActiveSection);
      window.removeEventListener('load', updateActiveSection);
      window.removeEventListener('scroll', updateActiveSection);
    };
  }, []);

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      const navContainer = document.querySelector('.nav-container');
      const headerHeight = navContainer?.getBoundingClientRect().height ?? 0;
      const sectionTop = section.getBoundingClientRect().top + window.scrollY - headerHeight - EXTRA_SCROLL_OFFSET;

      setMenuOpen(false); // Close the menu on mobile after clicking
      setActiveSectionId(sectionId);

      window.requestAnimationFrame(() => {
        window.scrollTo({
          top: Math.max(0, sectionTop),
          behavior: 'smooth'
        });
      });

      return;
    }

    setMenuOpen(false); // Close the menu on mobile after clicking
  };
  

  const renderCircles = () => {
    const activeIndex = Math.max(
      0,
      NAV_ITEMS.findIndex(({ id }) => id === activeSectionId)
    );

    return NAV_ITEMS.map(({ id, label }, index) => {
      const isCurrent = index === activeIndex;
      const isVisited = index < activeIndex;

      let IconComponent = GoCircle; // Default icon (unvisited)
      if (isVisited) {
        IconComponent = RiProgress8Fill; // Visited sections
      } else if (isCurrent) {
        IconComponent = RiProgress8Line; // Current section
      }

      return (
        <button
          key={id}
          type="button"
          className={`nav-item${isCurrent ? ' current' : ''}${isVisited ? ' visited' : ''}`}
          onClick={() => scrollToSection(id)}
          aria-current={isCurrent ? 'page' : undefined}
        >
          <div className="progress-icon">
            <IconComponent size={20} />  {/* Render the appropriate icon */}
          </div>
          <span className="nav-label">{label}</span>
        </button>
      );
    });
  };

  return (
    <header>
      <div className="nav-container">
        <button
          type="button"
          className="hamburger"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={menuOpen}
        >
          {menuOpen ? <RxCross1 size={30} /> : <GiHamburgerMenu size={30} />}  {/* Toggle between hamburger and cross icons */}
        </button>
        <nav className={`nav-menu ${menuOpen ? 'active' : ''}`}>
          <div className="nav-list">
            {renderCircles()}
          </div>
        </nav>
      </div>
    </header>
  );
}

export default Header;
